/**
 * The caller's catalog as one searchable list: catalog packs (shared curated
 * content) and their teams' folders, flattened. Every read carries the caller's
 * own token, so the CMS applies its normal membership and ownership checks and
 * the list is exactly what that caller may read.
 *
 * Plain `fetch`, no DOM and no engine, so a server runs it: the gateway's
 * `search_catalog` (SERVER_TOOLS) is this. Moved from the gateway's `cms.ts`.
 * Indexes are cached per caller so a turn with several searches does not
 * refetch the whole catalog: fresh for a minute, then served for up to 15 minutes
 * while a new one builds (see cachedCatalogIndex).
 */

export class CmsError extends Error {
    constructor(readonly status: number, path: string) {
        super(`CMS ${status} on ${path}`);
    }
}

/**
 * A read that has not answered by now has stalled: the slowest library listing on dev took 12 s,
 * measured from outside Azure. A stalled read would otherwise hold its caller past the ingress's
 * 240 s limit.
 */
const CMS_READ_TIMEOUT_MS = 60_000;

export async function cmsGet<T>(baseUrl: string, path: string, token: string): Promise<T> {
    const res = await fetch(`${baseUrl}${path}`, {
        headers: { authorization: `Bearer ${token}`, accept: 'application/json' },
        signal: AbortSignal.timeout(CMS_READ_TIMEOUT_MS),
    });
    if (!res.ok) {
        await res.body?.cancel();
        throw new CmsError(res.status, path);
    }
    return await res.json() as T;
}

// ── CMS response shapes (a subset of the generated DTOs in ./api) ──

export interface CmsAsset {
    assetId?: string;
    title?: string | null;
    originalFilename?: string | null;
    url?: string | null;
    thumbnailUrl?: string | null;
    entityType?: unknown;
    metadata?: unknown;
}

interface CatalogPack {
    id?: string;
    name?: string | null;
    catalogAssets?: CmsAsset[] | null;
    /**
     * How many assets the pack actually holds.
     *
     * `/catalogs/packs` returns a **five-asset preview** per pack regardless of
     * size, and this is the only field that says so. Without comparing the two,
     * a preview is indistinguishable from a complete pack.
     */
    totalAssetCount?: number | null;
}

interface UserTeam {
    teamId?: string;
    name?: string | null;
}

interface TeamFolders {
    folders?: { folderId?: string; title?: string | null }[] | null;
}

export interface IndexedAsset {
    id: string;
    title: string;
    filename: string;
    url: string;
    thumbnailUrl?: string;
    /** Pack name or "team / folder" path the asset came from. */
    source: string;
    sourceType: 'catalog' | 'folder';
    entityType?: unknown;
}

/** Swallow per-branch failures — one unreadable folder shouldn't empty the index. */
async function tolerant<T>(promise: Promise<T>, fallback: T, onDegraded?: () => void): Promise<T> {
    try {
        return await promise;
    } catch (err) {
        if (err instanceof CmsError && (err.status === 401 || err.status === 403)) throw err;
        // A 4xx other than a timeout or throttle is permanent (a folder that is gone, say), so
        // it is no reason to distrust the whole index; a 5xx or a network error is.
        const permanent = err instanceof CmsError && err.status < 500 && err.status !== 408 && err.status !== 429;
        if (!permanent) onDegraded?.();
        return fallback;
    }
}

/**
 * Fetch and flatten the caller's catalog: all packs + all folders of their
 * teams (or one team when scoped). Auth failures propagate (the token is
 * bad for everything); individual folder/pack failures degrade gracefully, and
 * `onDegraded` hears of each one, so a caller can tell a partial index from a whole one.
 */
export async function buildCatalogIndex(
    baseUrl: string,
    token: string,
    teamId?: string,
    onDegraded?: () => void,
): Promise<IndexedAsset[]> {
    const [packs, teams] = await Promise.all([
        tolerant(cmsGet<CatalogPack[]>(baseUrl, '/catalogs/packs', token), [], onDegraded),
        teamId
            ? Promise.resolve([{ teamId }] as UserTeam[])
            : cmsGet<UserTeam[]>(baseUrl, '/teams', token),
    ]);

    const assets: IndexedAsset[] = [];

    const push = (asset: CmsAsset, source: string, sourceType: 'catalog' | 'folder') => {
        if (!asset.assetId || !asset.url) return;
        assets.push({
            id: asset.assetId,
            title: asset.title ?? '',
            filename: asset.originalFilename ?? '',
            url: asset.url,
            thumbnailUrl: asset.thumbnailUrl ?? undefined,
            source,
            sourceType,
            entityType: asset.entityType,
        });
    };

    // The packs list is a PREVIEW, not a listing: it returns five assets per
    // pack however large the pack is, and reports the real size separately as
    // `totalAssetCount`. Measured on dev — 76 packs, every one listing exactly
    // five, 1343 assets in total. Trusting the preview indexed 380 of them and
    // left 72% of the model library invisible to scene generation, which reads
    // as "the catalog has nothing like that" rather than as a missing fetch.
    //
    // So fetch the detail whenever the preview is short of the stated total,
    // not only when it is empty. Bounded work: one extra request per pack, and
    // the whole index is cached (see cachedCatalogIndex).
    const fullPacks = await Promise.all(packs.map(pack => {
        const listed = pack.catalogAssets?.length ?? 0;
        // Short of the stated total, or listing nothing at all — the second is
        // the older case (some packs omit `catalogAssets`) and it survives the
        // `>= total` comparison, because a pack that reports no count reads as
        // 0 of 0 and would otherwise look complete while holding everything.
        const incomplete = listed === 0 || listed < (pack.totalAssetCount ?? listed);
        return pack.id && incomplete
            ? tolerant(cmsGet<CatalogPack>(baseUrl, `/catalogs/packs/${pack.id}`, token), pack, onDegraded)
            : Promise.resolve(pack);
    }));
    for (const pack of fullPacks) {
        for (const asset of pack.catalogAssets ?? []) push(asset, pack.name ?? 'Catalog', 'catalog');
    }

    await Promise.all(teams.map(async team => {
        if (!team.teamId) return;
        const none: TeamFolders = {};
        const teamFolders = await tolerant(cmsGet<TeamFolders>(baseUrl, `/teams/${team.teamId}/folders`, token), none, onDegraded);
        await Promise.all((teamFolders.folders ?? []).map(async folder => {
            if (!folder.folderId) return;
            const folderAssets = await tolerant(
                cmsGet<CmsAsset[]>(baseUrl, `/folders/${folder.folderId}/assets`, token),
                [],
                onDegraded,
            );
            const source = [team.name, folder.title].filter(Boolean).join(' / ') || 'Folder';
            for (const asset of folderAssets) push(asset, source, 'folder');
        }));
    }));

    return assets;
}

/** Keyword scoring: a term in the title counts twice a term in the filename or source. */
export function searchIndex(assets: IndexedAsset[], query: string, limit: number): IndexedAsset[] {
    const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (terms.length === 0) return assets.slice(0, limit);
    const scored = assets
        .map(asset => {
            const title = asset.title.toLowerCase();
            const rest = `${asset.filename} ${asset.source}`.toLowerCase();
            let score = 0;
            for (const term of terms) {
                if (title.includes(term)) score += 2;
                else if (rest.includes(term)) score += 1;
            }
            return { asset, score };
        })
        .filter(s => s.score > 0);
    scored.sort((a, b) => b.score - a.score);
    return scored.slice(0, limit).map(s => s.asset);
}

// ── Per-caller index cache ──────────────────────────────────
// A turn with several searches shouldn't refetch the catalog each time, and a
// long run shouldn't stall on a rebuild partway through: a cold build took 8-11 s
// on dev against 0.7 s warm. So an index is served as is for CACHE_FRESH_MS; up
// to CACHE_MAX_STALE_MS it is still served at once while one background build
// replaces it; older than that the caller waits for a new build. Memory per
// process is fine; the cost is at most CACHE_MAX_STALE_MS of staleness. A build
// the caller waits for is adopted as it comes, but a degraded one counts as
// already stale; a background refresh is held to a whole result, because it has
// an index to fall back on.

const CACHE_FRESH_MS = 60_000;
const CACHE_MAX_STALE_MS = 15 * 60_000;

interface CacheEntry {
    /** When `index` finished building (or, while the first build runs, when it started). */
    builtAt: number;
    index: Promise<IndexedAsset[]>;
    /** A build for this key is running; at most one at a time. */
    building: boolean;
    /** After a failed refresh, no new one starts before this. */
    retryAt: number;
}

const cache = new Map<string, CacheEntry>();

export function cachedCatalogIndex(
    baseUrl: string,
    token: string,
    userId: string,
    teamId?: string,
): Promise<IndexedAsset[]> {
    // Keyed by caller, not by CMS origin: one server talks to one CMS.
    const key = `${userId}|${teamId ?? '*'}`;
    const now = Date.now();
    const hit = cache.get(key);
    if (hit) {
        const age = now - hit.builtAt;
        const serveAsIs = age < CACHE_FRESH_MS || ((hit.building || now < hit.retryAt) && age < CACHE_MAX_STALE_MS);
        if (serveAsIs) return hit.index;
        if (age < CACHE_MAX_STALE_MS) {
            // Stale: answer from the old index and refresh behind it. A refresh that
            // fails, or comes back partial (buildCatalogIndex reads a 5xx as "nothing
            // there"), keeps the old index and stays quiet on purpose: the gateway has no
            // caller to tell, and a rejected token surfaces at the blocking rebuild after
            // CACHE_MAX_STALE_MS. The next try waits a minute. A hung read cannot pin
            // `building`: every CMS read times out (CMS_READ_TIMEOUT_MS).
            hit.building = true;
            let degraded = false;
            buildCatalogIndex(baseUrl, token, teamId, () => { degraded = true; }).then(
                fresh => {
                    hit.building = false;
                    // Only replace the entry this refresh started from.
                    if (cache.get(key) !== hit) return;
                    if (degraded) { hit.retryAt = Date.now() + CACHE_FRESH_MS; return; }
                    hit.index = Promise.resolve(fresh);
                    hit.builtAt = Date.now();
                },
                () => { hit.building = false; hit.retryAt = Date.now() + CACHE_FRESH_MS; },
            );
            return hit.index;
        }
    }
    // A long-running server meets many callers: drop the expired indexes rather than keep each one for good.
    for (const [other, entry] of cache) if (now - entry.builtAt >= CACHE_MAX_STALE_MS) cache.delete(other);
    let degraded = false;
    const index = buildCatalogIndex(baseUrl, token, teamId, () => { degraded = true; });
    const entry: CacheEntry = { builtAt: now, index, building: true, retryAt: 0 };
    index.then(
        () => {
            if (cache.get(key) !== entry) return;
            // A degraded build is served, but as already stale: the next call starts a refresh.
            entry.builtAt = degraded ? Date.now() - CACHE_FRESH_MS : Date.now();
            entry.building = false;
        },
        // A failed build must not be cached, but must not evict a newer build either.
        () => { if (cache.get(key) === entry) cache.delete(key); },
    );
    cache.set(key, entry);
    return index;
}

/** Full asset record by id (GET /assets/{id}), trimmed for tool output. */
export async function getAsset(baseUrl: string, token: string, assetId: string): Promise<Record<string, unknown>> {
    const asset = await cmsGet<CmsAsset & { description?: string | null }>(baseUrl, `/assets/${assetId}`, token);
    return {
        assetId: asset.assetId,
        title: asset.title,
        description: asset.description,
        originalFilename: asset.originalFilename,
        url: asset.url,
        thumbnailUrl: asset.thumbnailUrl,
        entityType: asset.entityType,
        metadata: asset.metadata,
    };
}
